import React from 'react';
import { useTranslation } from 'react-i18next'; // 1. Added import

const InventoryDashboard = ({
    inventoryItems,
    newInventoryItem,
    setNewInventoryItem,
    handleAddNewInventoryItem,
    handleUpdateInventory,
    loading
}) => {
    const { t } = useTranslation(); // 2. Initialized hook
    
    // --- Styles ---
    const panelStyle = {
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        marginBottom: '20px',
    };
    const inputStyle = { 
        width: '100%', 
        padding: '10px', 
        border: '1px solid #ccc', 
        borderRadius: '4px', 
        boxSizing: 'border-box',
    };
    const buttonStyle = { padding: '8px 15px', border: 'none', borderRadius: '4px', color: 'white', cursor: 'pointer', fontWeight: 'bold' };

    // 3. Localized Categories
    const categories = ['Linen', 'Bathroom Amenities', 'Cleaning Supplies', 'Maintenance', 'Kitchen', 'General'];

    const handleChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;
        if (name === 'currentStock' || name === 'lowStockThreshold') {
            finalValue = parseInt(value, 10) || 0;
        }
        setNewInventoryItem(p => ({ ...p, [name]: finalValue }));
    };

    const InventoryList = () => {
        const groupedItems = (inventoryItems || []).reduce((acc, item) => {
            const category = item.category || 'General';
            if (!acc[category]) acc[category] = [];
            acc[category].push(item);
            return acc;
        }, {});

        const sortedCategories = Object.keys(groupedItems).sort();

        return (
            <div style={panelStyle}>
                <h2 style={{ borderBottom: '2px solid #2ecc71', paddingBottom: '10px', color: '#2ecc71' }}>
                    <span role="img" aria-label="stock">📦</span> {t('inventory.current_stock')} ({inventoryItems.length})
                </h2>
                
                {sortedCategories.length === 0 && <p style={{ padding: '10px', textAlign: 'center', color: 'gray' }}>{t('inventory.no_items')}</p>}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    {sortedCategories.map(category => (
                        <div key={category}>
                            <h3 style={{ margin: '0 0 10px 0', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>
                                {t(`inventory.categories.${category.toLowerCase().replace(' ', '_')}`)} ({groupedItems[category].length} {t('inventory.items')})
                            </h3>
                            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '4px' }}> 
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                                            <th style={{ padding: '10px', textAlign: 'left' }}>{t('inventory.table.item')}</th>
                                            <th style={{ padding: '10px', textAlign: 'center' }}>{t('inventory.table.stock')}</th>
                                            <th style={{ padding: '10px', textAlign: 'center' }}>{t('inventory.table.threshold')}</th>
                                            <th style={{ padding: '10px', textAlign: 'center' }}>{t('inventory.table.alert')}</th>
                                            <th style={{ padding: '10px', textAlign: 'right' }}>{t('inventory.table.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupedItems[category].map(item => {
                                            const isLow = Number(item.currentStock) <= Number(item.lowStockThreshold);
                                            return (
                                                <tr key={item.id} style={{ borderBottom: '1px solid #eee', backgroundColor: isLow ? '#fef0f0' : 'white' }}>
                                                    <td style={{ padding: '10px', fontWeight: 'bold' }}>{item.name}</td>
                                                    <td style={{ padding: '10px', textAlign: 'center', color: isLow ? 'red' : '#28a745', fontWeight: 'bold' }}>
                                                        {item.currentStock}
                                                    </td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>{item.lowStockThreshold}</td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>
                                                        {isLow ? <span role="img" aria-label="alert">⚠️ {t('inventory.low_label')}</span> : 'OK'}
                                                    </td>
                                                    <td style={{ padding: '10px', textAlign: 'right' }}>
                                                        <input
                                                            type="number"
                                                            value={item.lowStockThreshold}
                                                            onChange={(e) => handleUpdateInventory(item.id, 'lowStockThreshold', parseInt(e.target.value, 10) || 0)}
                                                            style={{ ...inputStyle, width: '70px', padding: '5px', marginRight: '5px' }}
                                                            min="0"
                                                        />
                                                        <button
                                                            onClick={() => handleUpdateInventory(item.id, 'currentStock', parseInt(item.currentStock, 10) + 1)}
                                                            style={{ ...buttonStyle, backgroundColor: 'blue', padding: '5px 10px', marginRight: '5px' }}
                                                        >+1</button>
                                                        <button
                                                            onClick={() => handleUpdateInventory(item.id, 'currentStock', Math.max(0, parseInt(item.currentStock, 10) - 1))}
                                                            style={{ ...buttonStyle, backgroundColor: 'red', padding: '5px 10px' }}
                                                        >-1</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const AddItemForm = () => (
        <div style={panelStyle}>
            <h2 style={{ borderBottom: '2px solid #3498db', paddingBottom: '10px', color: '#3498db' }}>
                <span role="img" aria-label="add">➕</span> {t('inventory.add_title')}
            </h2>
            <form onSubmit={handleAddNewInventoryItem} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('inventory.form.name')}</label>
                    <input type="text" name="name" value={newInventoryItem.name || ''} onChange={handleChange} style={inputStyle} required />
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('inventory.form.category')}</label>
                    <select name="category" value={newInventoryItem.category || 'General'} onChange={handleChange} style={inputStyle} required>
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{t(`inventory.categories.${cat.toLowerCase().replace(' ', '_')}`)}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('inventory.form.stock')}</label>
                    <input type="number" name="currentStock" value={newInventoryItem.currentStock || ''} onChange={handleChange} style={inputStyle} min="0" required />
                </div>
                <div style={{ gridColumn: '1 / 2' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{t('inventory.form.threshold')}</label>
                    <input type="number" name="lowStockThreshold" value={newInventoryItem.lowStockThreshold || ''} onChange={handleChange} style={inputStyle} min="0" required />
                </div>
                <button type="submit" style={{ ...buttonStyle, backgroundColor: '#2ecc71', gridColumn: '3 / 4', alignSelf: 'end' }} disabled={loading}>
                    {t('inventory.form.submit')}
                </button>
            </form>
        </div>
    );

    return (
        <div style={{ padding: '20px' }}>
            <h1>{t('inventory.main_title')}</h1>
            {loading && <div style={{ color: 'var(--color-primary)', marginBottom: '10px' }}>{t('common.loading')}</div>}
            <AddItemForm />
            <InventoryList />
        </div>
    );
};

export default InventoryDashboard;